export const ALPHA_EVIDENCE_SCHEMA_VERSION = "sharemyai.alpha-evidence.v1";
export const ALPHA_MODEL_ID = "flux2-klein-4b-webgpu-int8";
export const ALPHA_RUNTIME_ID = "sharemyai-flux2-klein-int8-ort-webgpu@0.3.0";

/**
 * Runtimes accepted besides the current one.
 *
 * Empty, and meant to be. This is where a previous runtime sits during a
 * migration so that work already in flight is not invalidated the moment a new
 * renderer ships; once contributors have moved over it is emptied again, which
 * is what closes the door on the older pack.
 *
 * The last occupant was sharemyai-flux2-klein-q4-ort-jsep-webgpu@0.2.0, which
 * rendered with a third-party int4 export that declares no licence.
 */
export const LEGACY_RUNTIME_IDS = Object.freeze([]);
export const ALPHA_TARGET_RUNS = 30;
export const ALPHA_TARGET_COMPLETION_RATE = 0.95;
export const ALPHA_TARGET_DEVICE_CLASSES = 3;
export const ALPHA_TARGET_PAID_WORK_RUNS = 10;

const FAILURE_CODES = new Set(["aborted", "device_lost", "out_of_memory", "runtime_error", "unknown"]);

export function createAlphaEvidence(receipt) {
  const render = receipt?.modelProbe?.canonicalRender;
  if (!receipt?.id || !render || typeof render.success !== "boolean") return null;

  const capability = receipt.capability || {};
  const benchmark = receipt.benchmark || {};
  const adapter = capability.adapter || {};
  const runtimeId = render.runtimeId || receipt.modelProbe?.runtimeId || ALPHA_RUNTIME_ID;
  const success = render.success === true;

  return {
    schemaVersion: ALPHA_EVIDENCE_SCHEMA_VERSION,
    receiptId: receipt.id,
    recordedAt: receipt.createdAt,
    model: {
      id: receipt.modelProbe?.manifest?.id || ALPHA_MODEL_ID,
      runtimeId
    },
    workload: { width: 256, height: 256, steps: 4, seed: 424242 },
    device: {
      vendor: safeClass(adapter.vendor),
      architecture: safeClass(adapter.architecture),
      shaderF16: capability.features?.includes("shader-f16") === true,
      maxBufferMiB: bucketMiB(capability.limits?.maxBufferSize),
      maxStorageBindingMiB: bucketMiB(capability.limits?.maxStorageBufferBindingSize)
    },
    benchmark: {
      medianMs: roundTo(Number(benchmark.medianMs), 1),
      gflops: Math.round(Number(benchmark.gflops))
    },
    outcome: success ? {
      status: "success",
      durationMs: bucketDuration(render.durationMs),
      outputHash: String(render.outputHash || "").toLowerCase()
    } : {
      status: "failure",
      durationMs: bucketDuration(render.durationMs || 0),
      failureCode: FAILURE_CODES.has(render.failureCode) ? render.failureCode : "unknown"
    }
  };
}

export function classifyRenderFailure(error, aborted = false) {
  if (aborted) return "aborted";
  const message = String(error?.message || error || "").toLowerCase();
  if (message.includes("device") && message.includes("lost")) return "device_lost";
  if (message.includes("memory") || message.includes("allocation") || message.includes("oom")) return "out_of_memory";
  if (message) return "runtime_error";
  return "unknown";
}

function safeClass(value) {
  const normalized = String(value || "unknown").trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
  return normalized.slice(0, 40) || "unknown";
}

function bucketMiB(bytes) {
  const mib = Number(bytes) / 1024 ** 2;
  if (!Number.isFinite(mib) || mib <= 0) return 0;
  return 2 ** Math.floor(Math.log2(Math.max(1, Math.round(mib))));
}

function bucketDuration(value) {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.round(duration / 100) * 100;
}

function roundTo(value, digits) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}
